import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { changeTypesQuery, createChangeTemplate, markChangeType } from '../../api/changeManagement'
import { ApiError } from '../../api/client'
import {
  changeTicketTypeStatus,
  createTicketType,
  removeTicketType,
  ticketTypeListQuery,
  updateTicketType,
  type TicketTypeListRow,
} from '../../api/settingsEaseedeskTicketTypes'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Ticket types.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_types/settings/index.php` + `list.php`: doua liste de cartonase, cea
 * comuna cu chenar gri si cea a instantei cu chenar albastru, fiecare cu
 * insigna colorata, numele, butonul Overview si intrerupatorul de activare.
 *
 * DE CE DOUA LISTE. Tipurile din setul comun sunt randuri ale TUTUROR
 * instantelor: se pot aprinde si stinge, dar nu se pot redenumi si nu se pot
 * sterge - de-aia n-au butoanele acelea. Cele ale instantei le au.
 *
 * POARTA DE DINAINTEA PUBLICARII. La aprindere, serverul poate raspunde cu
 * lista lucrurilor care lipsesc in loc sa scrie. Atunci se intreaba, si numai
 * daca omul spune "da" se trimite din nou, cu `confirmed`. Nu e o interdictie:
 * uneori tipul se aprinde dinadins in timp ce se lucreaza la el.
 */

/**
 * `<input type="color">` cere `#rrggbb`; casuta de text de langa el poate fi
 * goala sau pe jumatate scrisa, si atunci pastila arata albastrul zonei.
 */
function swatch(color: string) {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#3c8dbc'
}

/** Clasele de change, in ordinea ITIL; 0 = tipul nu e change. */
const CHANGE_CLASSES: [number, string][] = [
  [0, 'Not a change'],
  [1, 'Standard change'],
  [2, 'Normal change'],
  [3, 'Emergency change'],
]

function TypeCard({
  type,
  shared,
  onReload,
  onEdit,
  changeClass = null,
}: {
  type: TicketTypeListRow
  shared: boolean
  onReload(): void
  onEdit(type: TicketTypeListRow): void
  /** null = omul nu poate citi setarile de change (sau inca se incarca): fara lista. */
  changeClass?: number | null
}) {
  const queryClient = useQueryClient()
  const mark = useMutation({
    mutationFn: (next: number) => markChangeType(type.ID, next),
    onSuccess: (answer) => {
      toast(answer.message, 'Change management')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'change_management', 'types'] })
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const [confirming, setConfirming] = useState<string[] | null>(null)
  /* `swal` din `settings/list-1.js`: stergerea INTREABA inainte. Butonul rosu
     de langa creion sterge randul furnizorului fara drum inapoi, si statea la
     un singur clic. */
  const [askDelete, setAskDelete] = useState(false)

  const flip = useMutation({
    mutationFn: (input: { active: boolean; confirmed: boolean }) =>
      changeTicketTypeStatus(type.ID, input.active, input.confirmed),
    onSuccess: (answer) => {
      if (answer.confirm) {
        setConfirming(answer.missing ?? [])
        return
      }
      toast('Setting changed', 'Success')
      onReload()
    },
    onError: (error) => {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => removeTicketType(type.scheme_id),
    onSuccess: () => {
      setAskDelete(false)
      toast('Ticket type removed', 'Success')
      onReload()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const border = shared ? '#DDD' : 'rgb(104, 170, 207)'
  const notReady = type.never_used
  const missing = type.missing.join(' and no ')

  const title = shared
    ? notReady
      ? 'This type has not been used in this instance yet'
      : 'Everything configured for this type'
    : notReady
      ? `Nothing is set up for this type yet - it has no ${missing}`
      : 'Everything configured for this type'

  return (
    <li
      className="list-li bg-white"
      style={{
        padding: '10px 20px',
        marginBottom: 4,
        borderRadius: 4,
        border: `1px solid ${border}`,
        borderLeft: `6px solid ${border}`,
        borderRight: `6px solid ${border}`,
      }}
    >
      <div className="row">
        <div className="col-md-8">
          <div style={{ minWidth: 50, float: 'left' }}>
            {/*
             * `od_console.css` da fiecarui `.badge` cerneala inchisa
             * (`--od-ink-soft`, #5b6672) si macheta scrie doar fundalul - deci
             * prescurtarea se citea inchis pe cercul colorat. In portal era
             * alba, din `.badge` al lui Bootstrap.
             */}
            <span
              className="badge pull-left"
              style={{ minWidth: 23, backgroundColor: type.color_identifier, color: '#fff', textAlign: 'center' }}
            >
              {type.short_name}
            </span>
          </div>
          <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
            {type.type_name}
          </h5>
        </div>
        <div className="col-md-4">
          {/*
           * Un rand de unelte, nu trei bucati plutite.
           *
           * In macheta erau trei `pull-right` unul dupa altul, iar randurile
           * comune n-au grupul de butoane deloc - asa ca aceeasi unealta cadea
           * in alt loc de la un rand la altul. Aici slotul butoanelor isi tine
           * latimea si cand e gol, deci intrerupatorul si Overview stau in
           * aceeasi coloana pe fiecare rand, cu sau fara butoane.
           */}
          <div
            className="od-tt-actions"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}
          >
            {/*
             * Slotul are 80px, nu 62: sub `body.od-console` un `.btn-xs` are
             * 10px pe laturi (od_console.css), deci cele doua butoane au
             * ~33 + 35px. In 62px, `btn-group` (butoane `float: left`) il
             * impingea pe Edit SUB Delete. Acum stau intr-un rand flex, fara
             * rupere, cu 6px intre ele - ca `.softsee-row-actions`.
             */}
            <div
              className="od-tt-edit"
              style={{ flex: '0 0 80px', display: 'flex', justifyContent: 'flex-end' }}
            >
              {shared ? null : (
                <div
                  className="od-tt-buttons"
                  role="group"
                  aria-label="controls"
                  style={{ display: 'inline-flex', flexWrap: 'nowrap', alignItems: 'center', gap: 6 }}
                >
                  {/*
                   * Glyphicons NU se servesc sub `/app`: cele doua butoane
                   * ieseau patrate goale. Font Awesome 5.15 se serveste, iar
                   * `fa-trash` si `fa-pencil-alt` chiar sunt in foaia adusa
                   * (`fa-pencil`, numele din FA 4, nu e - alt patrat gol).
                   */}
                  <button
                    type="button"
                    className="btn btn-danger btn-xs delete-type"
                    title="Delete this ticket type"
                    aria-label="Delete this ticket type"
                    disabled={remove.isPending}
                    onClick={() => setAskDelete(true)}
                  >
                    <i className="fas fa-trash" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-xs edit-type"
                    title="Edit this ticket type"
                    aria-label="Edit this ticket type"
                    onClick={() => onEdit(type)}
                  >
                    <i className="fas fa-pencil-alt" aria-hidden="true"></i>
                  </button>
                </div>
              )}
            </div>

            {/*
             * Change management (CEC L27): tichetele de tipul asta sunt
             * cereri de schimbare - primesc planul, fereastra, riscul si
             * aprobarea CAB pe fisa. Numai pentru tipurile folosite aici.
             */}
            {changeClass !== null && !type.never_used && (
              <select
                className="form-control input-sm od-tt-change"
                style={{ width: 150, flex: '0 0 auto' }}
                title="Treat tickets of this type as change requests"
                aria-label={type.type_name + ' change class'}
                value={changeClass}
                disabled={mark.isPending}
                onChange={(e) => mark.mutate(Number(e.target.value))}
              >
                {CHANGE_CLASSES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}

            <input
              type="checkbox"
              title={type.active ? 'Turn this ticket type off' : 'Turn this ticket type on'}
              aria-label={type.type_name + ' active'}
              checked={type.active}
              disabled={flip.isPending}
              onChange={(e) => flip.mutate({ active: e.target.checked, confirmed: false })}
            />

            <Link
              className="btn btn-default btn-xs"
              to="/settings/easeedesk/ticket_type_overview/$type"
              params={{ type: String(type.ID) }}
              style={notReady ? { opacity: 0.55 } : undefined}
              title={title}
              onClick={(e) => {
              /* Fisa unui tip fara flux si fara formulare n-are ce spune decat
                 "nimic": butonul o spune de aici, in loc s-o deschida goala. */
                if (notReady) {
                  e.preventDefault()
                  toast(
                    shared
                      ? 'This type has not been used in this instance yet.'
                      : `Nothing is set up for this type yet - it has no ${missing}.`,
                    'Nothing to show',
                  )
                }
              }}
            >
              <i className="fas fa-clipboard-list" aria-hidden="true"></i> Overview
            </Link>
          </div>
        </div>
      </div>

      <Modal
        id={`deleteTypeModal-${type.ID}`}
        labelledBy={`deleteTypeModalLabel-${type.ID}`}
        open={askDelete}
        onClose={() => setAskDelete(false)}
        title="Delete type ?"
      >
        <p>
          <strong>{type.type_name}</strong> will be removed from this instance.
        </p>
        <p className="color-light-grey">You will not be able to recover this type!</p>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={() => setAskDelete(false)}>
            Cancel
          </button>{' '}
          <button
            type="button"
            className="btn btn-danger confirm-delete-type"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        id={`confirmTypeModal-${type.ID}`}
        labelledBy={`confirmTypeModalLabel-${type.ID}`}
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title="This type is not ready yet"
      >
        <p>
          <strong>{type.type_name}</strong> is missing:
        </p>
        <ul>
          {(confirming ?? []).map((what) => (
            <li key={what}>{what}</li>
          ))}
        </ul>
        <p className="color-light-grey">
          A ticket of this type may stop in the agent&apos;s hands. Turn it on anyway?
        </p>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={() => setConfirming(null)}>
            Cancel
          </button>{' '}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setConfirming(null)
              flip.mutate({ active: true, confirmed: true })
            }}
          >
            Turn it on
          </button>
        </div>
      </Modal>
    </li>
  )
}

/**
 * `newTypeModal` din macheta.
 *
 * Tipul nou nu se naste gol: serverul ii scrie fluxul, New/Resolved/Closed,
 * pasii dintre ele si cate un formular pe fiecare. Cand instanta n-are din ce
 * porni - nu are statusuri comune de felul cerut - tipul RAMANE facut, doar
 * nemobilat, si ecranul o spune. Tacerea ar fi fost mai rea: omul ar fi crezut
 * ca e gata si ar fi aflat abia la primul tichet.
 */
function NewTypeModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const [name, setName] = useState('')
  const [color, setColor] = useState('')
  const [shortName, setShortName] = useState('')
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (!open) return
    setName('')
    setColor('')
    setShortName('')
    setHidden(false)
  }, [open])

  const save = useMutation({
    mutationFn: () =>
      createTicketType({
        type_name: name,
        type_color: color,
        type_short_name: shortName,
        type_hide: hidden,
      }),
    onSuccess: (answer) => {
      if (answer.furnished) {
        const built = answer.built
        toast(
          `Ticket type created, with a flow, ${built?.steps ?? 0} steps and ${built?.forms ?? 0} forms.`,
          'Success',
        )
        if (built && !built.closable) {
          toast('No final status was found, so a ticket of this type cannot be closed yet.', 'Check the sheet')
        }
      } else {
        toast(
          'Ticket type created, but nothing else could be set up: this instance has no shared New/Resolved to start from. Configure it from its sheet.',
          'Created, not furnished',
        )
      }
      onSaved()
      onClose()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="newTypeModal" labelledBy="newTypeModalLabel" open={open} onClose={onClose} title="New ticket type">
      <form
        id="new-type-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="type-name" className="control-label">
            Type name:
          </label>
          <input
            type="text"
            className="form-control"
            id="type-name"
            name="type_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="type-color" className="control-label">
            Type color:
          </label>
          {/* `colpick` din `settings/index-1.js` nu exista in v4, deci ramanea
              o casuta in care culoarea se scria pe de rost. Casuta nativa scrie
              acelasi camp, `type_color`. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              className="form-control"
              id="type-color"
              name="type_color"
              placeholder="#3c8dbc"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
            <input
              type="color"
              className="type-color-pick"
              aria-label="Type color"
              value={swatch(color)}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: 42, height: 34, padding: 2 }}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="type-short-name" className="control-label">
            Short name:
          </label>
          <input
            type="text"
            className="form-control"
            id="type-short-name"
            name="type_short_name"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="checkbox" className="control-label">
            Hide Ticket:{' '}
          </label>
          <input
            type="checkbox"
            id="checkbox"
            name="type_hide"
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
          />
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-type"
            disabled={name.trim() === '' || save.isPending}
          >
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** `editTypeModal` din macheta. */
function EditTypeModal({
  type,
  onClose,
  onSaved,
}: {
  type: TicketTypeListRow | null
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [color, setColor] = useState('')
  const [shortName, setShortName] = useState('')
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (!type) return
    setName(type.type_name)
    setColor(type.color_identifier)
    setShortName(type.short_name)
    setHidden(type.hidden)
  }, [type])

  const save = useMutation({
    mutationFn: () =>
      updateTicketType(type?.ID ?? 0, {
        type_name: name,
        type_color: color,
        type_short_name: shortName,
        type_hide: hidden,
      }),
    onSuccess: () => {
      toast('Ticket type saved.', 'Success')
      onSaved()
      onClose()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="editTypeModal"
      labelledBy="editTypeModalLabel"
      open={type !== null}
      onClose={onClose}
      title="Edit type"
    >
      <form
        id="edit-type-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="edit-type-name" className="control-label">
            Type name:
          </label>
          <input
            type="text"
            className="form-control"
            id="edit-type-name"
            name="type_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="edit-type-color" className="control-label">
            Type color:
          </label>
          {/* `colpick` din `settings/index-1.js` nu exista in v4, deci ramanea
              o casuta in care culoarea se scria pe de rost. Casuta nativa scrie
              acelasi camp, `type_color`. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              className="form-control"
              id="edit-type-color"
              name="type_color"
              placeholder="#3c8dbc"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
            <input
              type="color"
              className="type-color-pick"
              aria-label="Type color"
              value={swatch(color)}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: 42, height: 34, padding: 2 }}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="edit-type-short-name" className="control-label">
            Short name:
          </label>
          <input
            type="text"
            className="form-control"
            id="edit-type-short-name"
            name="type_short_name"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="type_hide" className="control-label">
            Hide Ticket:{' '}
          </label>
          <input
            type="checkbox"
            id="type_hide"
            name="type_hide"
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
          />
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={name.trim() === '' || save.isPending}>
            Update
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function TicketTypesPage() {
  const queryClient = useQueryClient()
  const page = useQuery(ticketTypeListQuery())
  const changes = useQuery({ ...changeTypesQuery(), retry: false })
  const template = useMutation({
    mutationFn: () => createChangeTemplate('Change Request'),
    onSuccess: (answer) => {
      toast(answer.message, 'Change management')
      reload()
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'change_management', 'types'] })
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })
  const classOf = (type: TicketTypeListRow): number | null =>
    changes.data?.types ? (changes.data.types[String(type.ID)] ?? 0) : null
  const [editing, setEditing] = useState<TicketTypeListRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [search, setSearch] = useState('')

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_types_settings'] })
  }

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed what="The ticket types" error={page.error} onRetry={() => void page.refetch()} />
  }
  if (!page.data) return <Spinner />

  /* `searchType()` din macheta: cauta in numele scris pe cartonas, in pagina.
     Lista de tipuri a unei instante e scurta, deci n-are rost un drum la
     server pentru fiecare litera. */
  const needle = search.trim().toLowerCase()
  const keep = (type: TicketTypeListRow) =>
    needle === '' ||
    type.type_name.toLowerCase().includes(needle) ||
    type.short_name.toLowerCase().includes(needle)

  return (
    <div>
      <SettingsHead
        strong="Ticket"
        rest="types"
        subtitle={
          <>
            Manage, create or edit ticket <strong>types</strong>
          </>
        }
        tools={
          <>
            {changes.data?.types && (
              <>
                <button
                  type="button"
                  className="btn btn-default od-tt-change-template"
                  title="A new ticket type, with its flow, marked as a Normal change"
                  disabled={template.isPending}
                  onClick={() => template.mutate()}
                >
                  <i className="fas fa-random" aria-hidden="true"></i> Change Request type
                </button>{' '}
                <Link className="btn btn-default" to="/settings/easeedesk/change_management">
                  <i className="fas fa-gavel" aria-hidden="true"></i> CAB &amp; risk
                </Link>{' '}
              </>
            )}
            <button type="button" className="btn btn-success" onClick={() => setCreating(true)}>
              <i className="fas fa-plus" aria-hidden="true"></i> Create ticket type
            </button>
          </>
        }
      />

      <div className="list-group"></div>

      <i
        className="fas fa-search"
        aria-hidden="true"
        style={{ fontSize: 10, position: 'absolute', marginTop: 12, marginLeft: 10 }}
      ></i>
      <input
        type="text"
        id="myInput"
        placeholder="Search for types.."
        title="Types"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '15%',
          fontSize: 15,
          padding: '5px 20px 5px 40px',
          border: '1px solid #ddd',
          marginBottom: 12,
        }}
      />

      <ul id="list">
        {page.data.shared.filter(keep).map((type) => (
          <TypeCard
            key={`shared-${type.ID}`}
            type={type}
            shared
            onReload={reload}
            onEdit={setEditing}
            changeClass={classOf(type)}
          />
        ))}
        {page.data.own.filter(keep).map((type) => (
          <TypeCard
            key={`own-${type.ID}`}
            type={type}
            shared={false}
            onReload={reload}
            onEdit={setEditing}
            changeClass={classOf(type)}
          />
        ))}
      </ul>

      <NewTypeModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
      <EditTypeModal type={editing} onClose={() => setEditing(null)} onSaved={reload} />

      <div className="well corner-flip flip-bg-white" data-color="inverse">
        <h4>
          {/* `body.od-console h4:not([class]) *` pune fontul de titlu peste tot
              ce e inauntru, iconita inclusa - si triunghiul iesea patrat gol.
              Scris in linie, ca sa nu se atinga foaia comuna a zonei. */}
          <i
            className="fas fa-exclamation-triangle"
            style={{ fontFamily: '"Font Awesome 5 Free"', fontWeight: 900 }}
          ></i>{' '}
          <strong>NOTE</strong>
        </h4>
        <p>
          You can create NEW ticket types by clicking <strong>CREATE</strong> button.
        </p>
        <div className="flip"></div>
      </div>
    </div>
  )
}
